<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando remito de compra...</p>
    </div>

    <div v-else class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Remitos de compra' }">Remitos de compra</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <template v-if="shippingPurchase.pos && shippingPurchase.number">
            <span class="view-header__breadcrumb-current">
              {{ padLeft(shippingPurchase.pos, 4) }}-{{ padLeft(shippingPurchase.number, 8) }}
            </span>
          </template>
          <span v-else class="view-header__breadcrumb-current">#{{ shippingPurchaseId }}</span>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de remito de compra</h1>
            <p class="view-header__subtitle">Información detallada del remito de compra.</p>
          </div>

          <div v-if="pdfUrl" class="view-header__actions">
            <button
              type="button"
              class="btn view-header__btn view-header__btn--primary"
              onclick="frames['frame'].print()"
            >
              <i class="bi bi-printer"></i>
              Imprimir
            </button>
            <iframe :src="pdfUrl" style="display:none;" name="frame"></iframe>
          </div>
        </div>
      </div>

      <!-- Header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-file-earmark-text"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de documento</span>
              <span class="invoice-type-value">
                Remito<template v-if="badgeLetter"> {{ badgeLetter }}</template>
              </span>
              <span v-if="shippingPurchase.letter && shippingPurchase.letter !== 'P'" class="invoice-type-badge">
                Original
              </span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-truck"></i>
              </span>
              <span>Datos del proveedor</span>
            </div>

            <div class="invoice-client-grid">
              <div class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ shippingPurchase.provider.name || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha de emisión
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="shippingPurchase.number && shippingPurchase.pos" class="client-field">
                <span class="field-label">
                  <i class="bi bi-file-text field-icon"></i>
                  N° de remito
                </span>
                <span class="field-value">
                  {{ padLeft(shippingPurchase.pos, 4) }}-{{ padLeft(shippingPurchase.number, 8) }}
                </span>
              </div>

              <div v-if="shippingPurchase.provider_cuit" class="client-field">
                <span class="field-label">
                  <i class="bi bi-card-text field-icon"></i>
                  CUIT
                </span>
                <span class="field-value">{{ shippingPurchase.provider_cuit }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasSecondaryInfo" class="invoice-header-secondary">
          <div v-if="shippingPurchase.response && shippingPurchase.response['CAE']" class="secondary-item">
            <span class="secondary-label">CAE</span>
            <span class="secondary-value secondary-value--mono">{{ shippingPurchase.response['CAE'] }}</span>
            <span v-if="caeExpDateFormatted" class="secondary-sublabel">
              (vto. {{ caeExpDateFormatted }})
            </span>
          </div>
        </div>
      </div>

      <!-- Items + notes -->
      <div class="row">
        <!-- Items table -->
        <div class="col-md-8 mb-4 mb-md-0">
          <div class="card items-card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-box-seam"></i>
                </span>
                <h5 class="mb-0">Items</h5>
              </div>
              <div v-if="shippingPurchase.shipping_note_details && shippingPurchase.shipping_note_details.length > 0" class="items-summary">
                <span class="items-summary-item">
                  Total de ítems: <strong>{{ shippingPurchase.shipping_note_details.length }}</strong>
                </span>
                <span v-if="totalUnits > 0" class="items-summary-sep">|</span>
                <span v-if="totalUnits > 0" class="items-summary-item">
                  Total de unidades: <strong>{{ totalUnits }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Item</th>
                      <th class="col-small text-center">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(value, idx) in shippingPurchase.shipping_note_details"
                      :key="idx"
                    >
                      <td>{{ value.description }}</td>
                      <td class="text-center">{{ value.quantity }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Notes panel -->
        <div class="col-md-4">
          <div v-if="shippingPurchase.notes" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota del proveedor</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ shippingPurchase.notes }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import getPdfmixin from "../../../mixins/getPdfmixin";

export default {
  props: ["shippingPurchaseId"],
  mixins: [getPdfmixin],
  data() {
    return {
      loading: true,
      pdfUrl: null,
      shippingPurchase: {
        letter: null,
        pos: null,
        number: null,
        provider_id: null,
        notes: '',
        shipping_note_details: [],
        provider: {
          name: null,
        },
      },
    };
  },
  computed: {
    createdDateFormatted() {
      if (!this.shippingPurchase.created_at) return '';
      return new Date(this.shippingPurchase.created_at).toLocaleDateString('es-AR');
    },
    badgeLetter() {
      const l = this.shippingPurchase.letter;
      if (!l || l === 'P') return 'R';
      return l;
    },
    caeExpDateFormatted() {
      const raw = this.shippingPurchase.response && this.shippingPurchase.response['CAEFchVto'];
      if (!raw) return '';
      const str = String(raw);
      if (/^\d{8}$/.test(str)) {
        return `${str.slice(6, 8)}/${str.slice(4, 6)}/${str.slice(0, 4)}`;
      }
      const d = new Date(str);
      return isNaN(d) ? str : d.toLocaleDateString('es-AR');
    },
    hasSecondaryInfo() {
      return !!(this.shippingPurchase.response && this.shippingPurchase.response['CAE']);
    },
    totalUnits() {
      if (!Array.isArray(this.shippingPurchase.shipping_note_details)) return 0;
      return this.shippingPurchase.shipping_note_details.reduce((sum, item) => {
        const q = Number(item.quantity) || 0;
        return sum + q;
      }, 0);
    },
  },
  mounted() {
    this.getShippingPurchaseDetail(this.shippingPurchaseId);
  },
  methods: {
    getShippingPurchaseDetail(shippingPurchaseId) {
      this.loading = true;
      this.$http
        .get('/api/shippingpurchases/detail/' + shippingPurchaseId)
        .then(response => {
          if (response.data.success) {
            this.shippingPurchase = response.data.data;
          }
        })
        .catch(error => {
          console.error('Error al cargar remito de compra:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    padLeft(number, length) {
      return number.toString().padStart(length, '0');
    },
  },
};
</script>
